import { styled } from '@mui/system'
import { colorPalette } from '../../../style/colorPalette'
import MoreVertIcon from '@mui/icons-material/MoreVertRounded'
import { useCallback, useMemo, useState, MouseEvent, useRef } from 'react'
import { ClickAwayListener, Divider, Paper, Popper } from '@mui/material'
import { useHistory } from 'react-router'
import { intl } from '../../../../i18n'
import Auth from '../../../../lib/commons/auth'

type EnterpriseProjectReportMenuProps = {
  projectCode: string
}

const MenuButtonWrapper = styled('button')({
  border: `1px solid ${colorPalette.monotone[2]}`,
  borderRadius: '4px',
  backgroundColor: colorPalette.monotone[0],
  width: '24px',
  height: '24px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  cursor: 'pointer',
})

const StyledMoreVertIcon = styled(MoreVertIcon)({
  color: colorPalette.monotone[4],
  height: '16px',
  width: '16px',
})

const MenuItemListWrapper = styled('div')({
  backgroundColor: colorPalette.monotone[0],
  display: 'flex',
  flexDirection: 'column',
  padding: '4px',
  borderRadius: '4px',
  boxShadow: `0px 4px 16px 0px ${colorPalette.monotone[4]}80`,
  width: '200px',
})

const MenuItemListHeader = styled('div')({
  color: colorPalette.monotone[5],
  fontSize: '12px',
  fontWeight: 600,
  padding: '8px',
})

const MenuItemListDivider = styled(Divider)({
  borderColor: colorPalette.monotone[2],
})

const MenuItemListArea = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  padding: '8px',
  gap: '4px',
})

const MenuItem = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '2px 0',
})

const MenuItemLink = styled('a')({
  color: colorPalette.monotone[4],
  fontSize: '12px',
  textDecoration: 'none',
})

const MenuItemList = ({ projectCode }: EnterpriseProjectReportMenuProps) => {
  const projectReportHref = useMemo(
    () => `${window.location.origin}/projectReport/${projectCode}`,
    [projectCode]
  )
  const versionReportHref = useMemo(
    () => `${window.location.origin}/project/versionReport/${projectCode}`,
    [projectCode]
  )
  return (
    <MenuItemListWrapper>
      <MenuItemListHeader>
        {intl.formatMessage({ id: 'menu' })}
      </MenuItemListHeader>
      <MenuItemListDivider />
      <MenuItemListArea>
        <MenuItem>
          <MenuItemLink href={projectReportHref} target="_blank">
            {intl.formatMessage({ id: 'to.projectReport' })}
          </MenuItemLink>
        </MenuItem>
        <MenuItem>
          <MenuItemLink href={versionReportHref} target="_blank">
            {intl.formatMessage({ id: 'to.versionReport' })}
          </MenuItemLink>
        </MenuItem>
      </MenuItemListArea>
    </MenuItemListWrapper>
  )
}

export const EnterpriseProjectReportMenu = ({
  projectCode,
}: EnterpriseProjectReportMenuProps) => {
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const active = useMemo(() => Boolean(anchorEl), [anchorEl])
  const onClick = useCallback(() => {
    if (!active) {
      setAnchorEl(buttonRef.current)
    } else {
      setAnchorEl(null)
    }
  }, [active])
  return (
    <>
      <MenuButtonWrapper ref={buttonRef} onClick={onClick}>
        <StyledMoreVertIcon />
      </MenuButtonWrapper>
      <Popper open={active} anchorEl={anchorEl} placement="bottom-end">
        <ClickAwayListener onClickAway={onClick}>
          <Paper>
            <MenuItemList projectCode={projectCode} />
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  )
}
